import { useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useSnackbar } from 'notistack'

import { buildTestDmpExistenceCommand, buildOpenDmpCommand, buildSetMssPatientInfosCommand } from '../lib/commandBuilder'
import { sendEfficienceCommand } from '../lib/helpers'
import type { DmpStatus } from './types'

type UseDMPFlowParams = {
  iframe: HTMLIFrameElement | null
  isLoggedIn: boolean
  patientInsNIR?: string
  mssClientMode?: boolean
}

/**
 * Manages the DMP command flow:
 * 1. testDmpExistence when logged in
 * 2. openDmp when DMP exists
 */
export function useDmpFlow({ iframe, isLoggedIn, patientInsNIR, mssClientMode = false }: UseDMPFlowParams) {
  const testDmpReqId = useMemo(() => uuidv4(), [])
  const openDmpReqId = useMemo(() => uuidv4(), [])
  const [dmpStatus, setDmpStatus] = useState<DmpStatus>('checking')

  // Effect 1: Send testDmpExistence when logged in
  useEffect(() => {
    if (!iframe?.contentWindow || !isLoggedIn || !patientInsNIR || mssClientMode) return
    if (dmpStatus !== 'checking') return

    console.log('[DmpFlow] Sending testDmpExistence command')
    const xml = buildTestDmpExistenceCommand(testDmpReqId, 'T', patientInsNIR)
    sendEfficienceCommand(iframe, xml)
  }, [iframe, isLoggedIn, dmpStatus, testDmpReqId, patientInsNIR, mssClientMode])

  // Effect 2: Send openDmp when DMP exists
  useEffect(() => {
    if (!iframe?.contentWindow || dmpStatus !== 'exists' || !patientInsNIR || mssClientMode) return

    console.log('[DmpFlow] DMP exists, sending openDmp command')
    const xml = buildOpenDmpCommand(openDmpReqId, 'T', patientInsNIR)
    sendEfficienceCommand(iframe, xml)
  }, [iframe, dmpStatus, openDmpReqId, patientInsNIR, mssClientMode])

  return {
    dmpStatus,
    setDmpStatus,
    testDmpReqId,
    openDmpReqId,
  }
}
